<script setup lang="ts">
/**
 * Дерево страниц в сайдбаре вместе с тем, что с ними можно сделать: завести,
 * перенести, удалить (макет 23:53 — строка «Новая страница» под деревом).
 *
 * Диалоги живут здесь, а не в дереве: дерево рекурсивное, и модалка внутри
 * него открылась бы столько раз, сколько уровней у страницы.
 */
import { AppButton, AppModal, AppSelect, useToast } from '@vinprop/design-system'
import { Plus } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { errorMessage } from '@/api/errors'

import { withDescendants, type PageNode } from '../api'
import { useKnowledgeStore } from '../store'
import KbTree, { type TreeAction } from './KbTree.vue'

defineProps<{ activeId: string }>()

const { t } = useI18n()
const router = useRouter()
const store = useKnowledgeStore()
const toast = useToast()

const moving = ref<PageNode | null>(null)
const removing = ref<PageNode | null>(null)
const parentId = ref('')
const busy = ref(false)

/**
 * Куда можно перенести: любая страница пространства, кроме самой переносимой и
 * её потомков — иначе поддерево оторвётся от корня.
 */
const parentOptions = computed(() => {
  const forbidden = moving.value ? withDescendants(store.pages, moving.value.id) : new Set<string>()

  return [
    { value: '', label: t('kb.tree.root') },
    ...store.pages
      .filter((page) => !forbidden.has(page.id))
      .map((page) => ({ value: page.id, label: page.title })),
  ]
})

const create = async (parent: string) => {
  try {
    const page = await store.create(parent, t('kb.tree.newPageTitle'))

    await router.push(`/kb/${page.id}/edit`)
  } catch (failure) {
    toast.error(errorMessage(failure, t('kb.tree.createFailed')))
  }
}

const onAction = (action: TreeAction, node: PageNode) => {
  if (action === 'child') return void create(node.id)

  if (action === 'move') {
    moving.value = node
    parentId.value = node.parentId

    return
  }

  removing.value = node
}

const move = async () => {
  if (!moving.value) return

  busy.value = true

  try {
    await store.move(moving.value.id, parentId.value)
    moving.value = null
  } catch (failure) {
    toast.error(errorMessage(failure, t('kb.tree.moveFailed')))
  } finally {
    busy.value = false
  }
}

const remove = async () => {
  if (!removing.value) return

  const { id } = removing.value

  busy.value = true

  try {
    await store.remove(id)
    removing.value = null
    await router.push('/kb')
  } catch (failure) {
    toast.error(errorMessage(failure, t('kb.tree.deleteFailed')))
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="kbside">
    <KbTree :nodes="store.tree" :active-id="activeId" :on-action="onAction" />

    <button type="button" class="kbside__new" @click="create('')">
      <Plus :size="14" />
      <span>{{ t('kb.tree.newPage') }}</span>
    </button>
  </div>

  <AppModal
    v-if="moving"
    model-value
    :title="t('kb.tree.moveTitle', { title: moving.title })"
    size="small"
    @update:model-value="moving = null"
  >
    <AppSelect v-model="parentId" :options="parentOptions" :label="t('kb.tree.newParent')" />

    <template #footer>
      <AppButton variant="secondary" @click="moving = null">
        {{ t('common.actions.cancel') }}
      </AppButton>
      <AppButton :loading="busy" @click="move">{{ t('kb.tree.move') }}</AppButton>
    </template>
  </AppModal>

  <AppModal
    v-if="removing"
    model-value
    :title="t('kb.tree.deleteTitle', { title: removing.title })"
    size="small"
    @update:model-value="removing = null"
  >
    <!-- Удаление уносит и вложенные страницы: об этом предупреждаем до, а не
         после — восстановить их из интерфейса нельзя. -->
    <p class="kbside__warning">
      {{ removing.children.length ? t('kb.tree.deleteWithChildren') : t('kb.tree.deleteHint') }}
    </p>

    <template #footer>
      <AppButton variant="secondary" @click="removing = null">
        {{ t('common.actions.cancel') }}
      </AppButton>
      <AppButton variant="danger" :loading="busy" @click="remove">
        {{ t('kb.tree.delete') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.kbside {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.kbside__new {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 30px;
  padding: 0 8px;
  border: none;
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  cursor: pointer;
}

.kbside__new:hover {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
}

.kbside__warning {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
}
</style>
